import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { CATEGORY_LABELS, CAUSE_LABELS, PRIORITY_LABELS, percent, ticketErrorMessage } from "../../lib/tickets";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";

const PRESETS = [{ days: 7, label: "7 jours" }, { days: 30, label: "30 jours" }, { days: 90, label: "90 jours" }];

function isoDay(date) {
  return date.toISOString().slice(0, 10);
}

function presetRange(days, now = new Date()) {
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: isoDay(from), to: isoDay(to) };
}

function Figure({ label, value, hint, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

// One breakdown as labelled bars (a list, readable without colour); counts only, never a ticket's text.
function Breakdown({ title, counts, labels, testid }) {
  const entries = Object.entries(counts || {});
  const max = Math.max(1, ...entries.map(([, n]) => n));
  return (
    <Card className="p-5 space-y-3" data-testid={testid}>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {entries.length === 0 ? <p className="text-sm text-slate-500">Rien sur la période.</p> : (
        <ul className="space-y-2">
          {entries.map(([key, n]) => (
            <li key={key} className="text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-slate-700 truncate">{labels[key] || (key === "unknown" ? "Non renseignée" : key)}</span>
                <span className="tabular-nums text-slate-900 font-medium">{n}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 mt-1" aria-hidden="true">
                <div className="h-full rounded-full bg-jamora" style={{ width: `${(n / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// « Indicateurs » of the tickets page (D-52, tickets:view_all): what the support team needs to steer its delays and its load.
export default function TicketStats() {
  const [range, setRange] = useState(() => presetRange(30));
  const [state, setState] = useState({ phase: "loading", data: null, error: "" });
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: "loading" }));
    try {
      const { data } = await api.get("/tickets/stats", { params: range });
      if (mine === seq.current) setState({ phase: "ready", data, error: "" });
    } catch (e) {
      if (mine === seq.current) setState({ phase: "error", data: null, error: ticketErrorMessage(e, "Impossible de calculer les indicateurs.") });
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const preset = PRESETS.find((p) => { const r = presetRange(p.days); return r.from === range.from && r.to === range.to; });
  const d = state.data;

  return (
    <div className="space-y-4" data-testid="tickets-stats">
      <div className="flex flex-wrap items-end gap-2">
        {PRESETS.map((p) => (
          <Button key={p.days} size="sm" variant={preset?.days === p.days ? "default" : "outline"} aria-pressed={preset?.days === p.days}
            onClick={() => setRange(presetRange(p.days))} data-testid={`tickets-stats-${p.days}`}>{p.label}</Button>
        ))}
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="tickets-stats-from" className="text-xs">Du</Label>
            <Input id="tickets-stats-from" type="date" className="h-9 w-40" value={range.from} max={range.to}
              onChange={(e) => e.target.value && setRange((r) => ({ ...r, from: e.target.value }))} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tickets-stats-to" className="text-xs">Au</Label>
            <Input id="tickets-stats-to" type="date" className="h-9 w-40" value={range.to} min={range.from}
              onChange={(e) => e.target.value && setRange((r) => ({ ...r, to: e.target.value }))} />
          </div>
        </div>
      </div>

      {state.phase === "loading" && !d && <div className="space-y-2"><Skeleton className="h-28 w-full" /><Skeleton className="h-40 w-full" /></div>}
      {state.phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="tickets-stats-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{state.error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {d && state.phase !== "error" && (
        <div className={`space-y-4 ${state.phase === "loading" ? "opacity-60" : ""}`}>
          {d.partial && <p className="text-sm text-amber-700">Période très chargée : les chiffres portent sur les 10 000 premiers tickets.</p>}
          <Card className="p-5 grid gap-5 grid-cols-2 lg:grid-cols-4">
            <Figure label="Tickets ouverts sur la période" value={d.created} testid="tickets-stats-created" />
            <Figure label="Tickets résolus" value={d.resolved} testid="tickets-stats-resolved" />
            <Figure label="En cours aujourd'hui" value={d.open_now} />
            <Figure label="Satisfaction moyenne" value={d.satisfaction.average === null ? "—" : `${d.satisfaction.average.toLocaleString("fr-FR")}/5`}
              hint={`${d.satisfaction.count} note${d.satisfaction.count > 1 ? "s" : ""}`} />
            <Figure label="Première réponse dans les temps" value={percent(d.first_response_met_rate)} testid="tickets-stats-first-rate"
              hint={d.median_first_response_minutes === null ? null : `Médiane : ${d.median_first_response_minutes} min`} />
            <Figure label="Résolution dans les temps" value={percent(d.resolution_met_rate)} testid="tickets-stats-resolution-rate"
              hint={d.median_resolution_hours === null ? null : `Médiane : ${d.median_resolution_hours.toLocaleString("fr-FR")} h`} />
            <Figure label="Tickets rouverts" value={percent(d.reopen_rate)} hint="parmi les tickets résolus" />
          </Card>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Breakdown title="Par catégorie" counts={d.by_category} labels={CATEGORY_LABELS} testid="tickets-stats-category" />
            <Breakdown title="Par priorité" counts={d.by_priority} labels={PRIORITY_LABELS} testid="tickets-stats-priority" />
            <Breakdown title="Causes des tickets résolus" counts={d.by_cause} labels={CAUSE_LABELS} testid="tickets-stats-cause" />
          </div>
          <Card className="p-5 space-y-2" data-testid="tickets-stats-pharmacies">
            <h3 className="text-sm font-semibold text-slate-900">Pharmacies qui ont ouvert le plus de tickets</h3>
            {d.top_pharmacies.length === 0 ? <p className="text-sm text-slate-500">Rien sur la période.</p> : (
              <ol className="divide-y divide-slate-100">
                {d.top_pharmacies.map((p) => (
                  <li key={p.prospect_id} className="flex justify-between gap-3 py-2 text-sm">
                    <Link to={`/support/tickets?prospect=${encodeURIComponent(p.prospect_id)}&state=open`} className="text-jamora hover:underline truncate">{p.name}</Link>
                    <span className="tabular-nums font-medium">{p.count}</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
